import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Collapse
} from '@mui/material'
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material'
import { poolwerxColors } from '../../../../theme/colors'
import { getMarketFilterOptions } from '../../../../constants/markets'
import { Filters } from '../hooks/useFranchisePartnersFilters'
import { apiClient } from '../../../../services/api'

interface RegionOption {
  region_code: string
  region_name: string
  market: string
}

interface BdmOption {
  id: number
  name: string
  market: string
}

interface FilterCardProps {
  filters: Filters
  onFilterChange: (key: keyof Filters, value: string) => void
  onClearFilters: () => void
  loading?: boolean
}

// Label style for filter fields
const labelStyle = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.75rem',
  mb: 0.5,
  display: 'block'
}

const FilterCard: React.FC<FilterCardProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  loading = false
}) => {
  const marketOptions = getMarketFilterOptions()
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([])
  const [bdmOptions, setBdmOptions] = useState<BdmOption[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [loadingBdms, setLoadingBdms] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Fetch regions when market changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (!filters.market) {
        setRegionOptions([])
        return
      }
      setLoadingRegions(true)
      try {
        const response = await apiClient.get(`/admin/regions?market=${filters.market}&per_page=100`)
        setRegionOptions(response.data.regions || [])
      } catch (error) {
        console.error('Failed to fetch regions:', error)
        setRegionOptions([])
      } finally {
        setLoadingRegions(false)
      }
    }
    fetchRegions()
  }, [filters.market])

  // Fetch all BDMs on mount (independent of market)
  useEffect(() => {
    const fetchBdms = async () => {
      setLoadingBdms(true)
      try {
        const response = await apiClient.get('/admin/bdm-options')
        setBdmOptions(response.data || [])
      } catch (error) {
        console.error('Failed to fetch BDMs:', error)
        setBdmOptions([])
      } finally {
        setLoadingBdms(false)
      }
    }
    fetchBdms()
  }, [])

  // Clear dependent filters when market changes (only region, not bdm)
  const handleMarketChange = (value: string) => {
    onFilterChange('market', value)
    // Clear region when market changes (BDM is independent)
    if (filters.region) onFilterChange('region', '')
  }

  // Check if any secondary (Line 2) filters are active
  const hasSecondaryFilters =
    filters.bdm !== '' ||
    filters.systemStatus !== ''

  // Show Line 2 when expanded OR when any secondary filter is active
  const showSecondaryFilters = expanded || hasSecondaryFilters

  const hasActiveFilters =
    filters.textSearch !== '' ||
    filters.market !== '' ||
    filters.region !== '' ||
    filters.bdm !== '' ||
    filters.fpStatus !== '' ||
    filters.systemStatus !== ''

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Line 1: Search, FP Status, Market, Region */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography component="label" sx={labelStyle}>
              Search for Franchise Partners
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or FP code"
              variant="outlined"
              value={filters.textSearch}
              onChange={(e) => onFilterChange('textSearch', e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography component="label" sx={labelStyle}>
              FP Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={filters.fpStatus}
                onChange={(e) => onFilterChange('fpStatus', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Onboarding">Onboarding</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Terminated">Terminated</MenuItem>
                <MenuItem value="Test">Test</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography component="label" sx={labelStyle}>
              Market
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                value={filters.market}
                onChange={(e) => handleMarketChange(e.target.value)}
              >
                <MenuItem value="">All Markets</MenuItem>
                {marketOptions.filter(opt => opt.value !== '').map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography component="label" sx={labelStyle}>
              Region
            </Typography>
            <FormControl fullWidth size="small" disabled={!filters.market || loadingRegions}>
              <Select
                displayEmpty
                value={filters.region}
                onChange={(e) => onFilterChange('region', e.target.value)}
              >
                <MenuItem value="">All Regions</MenuItem>
                {regionOptions.map(option => (
                  <MenuItem key={option.region_code} value={option.region_code}>
                    {option.region_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Line 2: ROM/FBC, System Status (collapsible) */}
        <Collapse in={showSecondaryFilters}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <Typography component="label" sx={labelStyle}>
                ROM/FBC
              </Typography>
              <FormControl fullWidth size="small" disabled={loadingBdms}>
                <Select
                  displayEmpty
                  value={filters.bdm}
                  onChange={(e) => onFilterChange('bdm', e.target.value)}
                >
                  <MenuItem value="">Select ROM/FBC</MenuItem>
                  {bdmOptions.map(option => (
                    <MenuItem key={option.id} value={option.id.toString()}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography component="label" sx={labelStyle}>
                System Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={filters.systemStatus}
                  onChange={(e) => onFilterChange('systemStatus', e.target.value)}
                >
                  <MenuItem value="">Select System Status</MenuItem>
                  <MenuItem value="None">None</MenuItem>
                  <MenuItem value="Provisioning">Provisioning</MenuItem>
                  <MenuItem value="Live">Live</MenuItem>
                  <MenuItem value="Retiring">Retiring</MenuItem>
                  <MenuItem value="Ended">Ended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>

        {/* Line 3: More/Less Filters button (left), Clear Filters button (right) */}
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
          <Grid item xs={6}>
            {!hasSecondaryFilters && (
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ color: 'text.secondary' }}
              >
                {expanded ? 'Less Filters' : 'More Filters'}
              </Button>
            )}
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  onClearFilters()
                  setExpanded(false)
                }}
                disabled={!hasActiveFilters}
                sx={{
                  borderColor: poolwerxColors.red,
                  color: poolwerxColors.red,
                  '&:hover': {
                    borderColor: poolwerxColors.redHover,
                    backgroundColor: poolwerxColors.redLight,
                    color: poolwerxColors.redHover
                  },
                  '&:disabled': {
                    borderColor: 'action.disabled',
                    color: 'action.disabled'
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default FilterCard
